import {Autocomplete, type AutocompleteProps} from '@mui/material';
import {
  createTheme,
  type ThemeOptions,
  ThemeProvider,
} from '@mui/material/styles';
import React from 'react';

import {MUI_LAYER_THEME} from '../../../util/layers';
import {THEME} from '../../common/css/color.styles';
import {hexToRGB, RED_550} from '../../common/css/globals.styles';

const HEIGHTS = {
  small: '24px',
  medium: '32px',
  large: '40px',
  variable: undefined,
};

const FONT_SIZES = {
  small: '14px',
  medium: '16px',
  large: '16px',
  variable: '14px',
};

const getStyles = (props: AdditionalProps) => {
  const size = props.size ?? 'medium';
  const options: ThemeOptions = {
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              paddingTop: '0px !important',
              paddingBottom: '0px !important',
              fontSize: FONT_SIZES[size],
              fontFamily: "'Source Sans 3'",
              color: THEME.textPrimary,
              maxWidth: props.maxWidth ? `${props.maxWidth}px` : '100%',
              minWidth: props.minWidth ? `${props.minWidth}px` : '100px',
              '& fieldset': {
                borderColor: THEME.border,
              },
              '&.Mui-focused fieldset': {
                borderColor: THEME.borderFieldFocus,
                borderWidth: '2px',
              },
              '&:hover:not(.Mui-focused) fieldset': {
                borderColor: THEME.borderFieldHover,
                borderWidth: '2px',
              },
              '&.Mui-error fieldset': {
                borderColor: `light-dark(${RED_550}, var(--red-450))`,
              },
              '&.Mui-error.Mui-focused fieldset, &.Mui-error:hover:not(.Mui-focused) fieldset':
                {
                  borderColor: `light-dark(${RED_550}, var(--red-450))`,
                  borderWidth: '2px',
                },
              '&.Mui-error': {
                boxShadow: `0 0 0 3px light-dark(${hexToRGB(RED_550, 0.2)}, color-mix(in srgb, var(--red-450) 20%, transparent))`,
              },
              '& .MuiInputBase-input::placeholder': {
                color: THEME.textTertiary,
                opacity: 1,
              },
              '& .MuiInputBase-input': {
                padding: '0px',
                minHeight: `${HEIGHTS[size]} !important`,
              },
            },
            '& .MuiAutocomplete-popupIndicator': {
              borderRadius: '4px',
              padding: '4px',
            },
            '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root':
              {
                paddingRight: props.hasInputValue ? '28px' : '0px', // Apply padding only if input exists
              },
          },
          loading: {
            fontFamily: "'Source Sans 3'",
            fontSize: FONT_SIZES[size],
            color: THEME.textTertiary,
          },
          noOptions: {
            fontFamily: "'Source Sans 3'",
            fontSize: FONT_SIZES[size],
            color: THEME.textTertiary,
          },
          option: {
            borderColor: THEME.border,
            borderWidth: '1px',
            paddingLeft: '10px !important',
            margin: '0 6px',
            borderRadius: '4px',
            cursor: 'pointer',
            backgroundColor: 'transparent',
            color: THEME.textPrimary,
            fontSize: FONT_SIZES[size],
            '&[aria-selected="true"]': {
              backgroundColor: `${THEME.bgOptionSelected} !important`,
              color: THEME.textOptionSelected,
            },
            '&:focus-within[aria-selected="true"]': {
              backgroundColor: `${THEME.bgOptionSelected} !important`,
              color: THEME.textOptionSelected,
            },
            '&:hover': {
              backgroundColor: 'light-dark(var(--moon-100), var(--moon-900))',
            },
          },
          clearIndicator: {
            overflow: 'hidden',
            borderRadius: '4px',
            width: '24px',
            height: '24px',
            color: THEME.textSecondary,
            '&:hover': {
              backgroundColor: 'light-dark(#f5f5f5, var(--moon-900))',
            },
          },
          endAdornment: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          inputRoot: {
            '& .MuiInputBase-inputMultiline': {
              overflow: 'hidden',
              whiteSpace: 'pre-wrap',
              overflowY: 'auto',
              scrollbarWidth: 'none', // For Firefox (hides the scrollbar)
              msOverflowStyle: 'none', // For IE and Edge (hides the scrollbar)
              '&::-webkit-scrollbar': {
                display: 'none', // For Chrome, Safari, and WebKit-based browsers (hides the scrollbar)
              },
            },
          },
          // menu dropdown
          paper: {
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.16)',
            backgroundColor: THEME.bgSurfaceRaised,
            border: `1px solid ${THEME.border}`,
            borderRadius: '4px',
            // MUI 6.4+ always mounts this paper; hide it when freeSolo leaves it empty
            '&:empty': {
              display: 'none',
            },
          },
        },
      },
    },
  };
  // A from-scratch theme resets zIndex to MUI defaults; keep the popper on
  // the @wandb/components layer scale (util/layers.ts).
  return createTheme(options, MUI_LAYER_THEME);
};
const SelectSizes = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
  Variable: 'variable',
} as const;

type SelectSize = (typeof SelectSizes)[keyof typeof SelectSizes];

type AdditionalProps = {
  autocompleteRef?: React.MutableRefObject<HTMLDivElement | null>;
  hasInputValue?: boolean;
  isDarkMode?: boolean;
  maxWidth?: number;
  minWidth?: number;
  size?: SelectSize;
};

export const AutoComplete = <
  Option,
  Multiple extends boolean = boolean,
  DisableClearable extends boolean = boolean,
  FreeSolo extends boolean = boolean,
>(
  props: AutocompleteProps<Option, Multiple, DisableClearable, FreeSolo> &
    AdditionalProps
) => {
  const {
    autocompleteRef,
    hasInputValue,
    isDarkMode,
    maxWidth,
    minWidth,
    size,
    ...safeProps // we're just destructuring the other values out since they're unrecognized by the autocomplete component
  } = props;
  return (
    // oxlint-disable-next-line react/refs
    <ThemeProvider theme={getStyles(props)}>
      {/* oxlint-disable-next-line react/refs */}
      <Autocomplete ref={props.autocompleteRef} {...safeProps} />
    </ThemeProvider>
  );
};
